import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth";
import { admin } from "@/lib/api";
import { Bell, Menu, CheckCircle2, Loader2, XCircle, ChevronDown, Sun, Moon } from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import { cn } from "@/components/ui/cn";
import { formatDateTime } from "@/lib/format";
import { useThemeStore } from "@/stores/theme";

const TITLES: Array<{ match: RegExp; title: string }> = [
  { match: /^\/dashboard/, title: "Dashboard" },
  { match: /^\/leads\/[^/]+/, title: "Lead detail" },
  { match: /^\/leads/, title: "Job Leads" },
  { match: /^\/hackathons\/[^/]+/, title: "Hackathon detail" },
  { match: /^\/hackathons/, title: "Hackathon Leads" },
  { match: /^\/job-fairs/, title: "Job Fairs" },
  { match: /^\/my-leads/, title: "My Leads" },
  { match: /^\/campus-intel/, title: "Campus Intel" },
  { match: /^\/intelligence/, title: "Intelligence" },
  { match: /^\/college-data/, title: "College Data" },
  { match: /^\/tpo-directory/, title: "TPO Directory" },
  { match: /^\/chro-directory/, title: "CHRO Directory" },
  { match: /^\/companies/, title: "Companies" },
  { match: /^\/contacts/, title: "HR Contacts" },
  { match: /^\/settings/, title: "Settings" },
  { match: /^\/team/, title: "Team" },
  { match: /^\/armies/, title: "Scraper Armies" },
];

function usePageTitle(pathname: string): string {
  for (const t of TITLES) {
    if (t.match.test(pathname)) return t.title;
  }
  return "HireGen";
}

interface Run {
  id: string;
  run_type?: string | null;
  status?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  error?: string | null;
}

const Header = ({ onMenuClick }: { onMenuClick?: () => void }) => {
  const { user, isOffline, logout, revokeCurrentToken } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const navigate = useNavigate();
  const location = useLocation();
  const title = usePageTitle(location.pathname);

  const isAdmin = user?.role === "admin";

  const [notifOpen, setNotifOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  const enabled = !!user && !isOffline;

  // Global live army status: every page shows whether the fleet is working.
  // Shared query key ['army-status'] so Dashboard/Leads polls and this one hit
  // the same cache instead of three independent 5s setInterval loops.
  const { data: armyStatusData, isError: armyError } = useQuery({
    queryKey: ["army-status"],
    queryFn: () => admin.armyStatus() as Promise<{ raw?: number; enrichment?: number; verification?: number; draft?: number; halted?: boolean }>,
    enabled,
    refetchInterval: enabled ? 5000 : false,
    retry: false,
    // A failed refresh keeps the last known status; never flash a wrong state.
    placeholderData: (prev) => prev,
  });
  const armyQueued = (armyStatusData?.raw || 0) + (armyStatusData?.enrichment || 0) + (armyStatusData?.verification || 0) + (armyStatusData?.draft || 0);
  const armyHalted = !!armyStatusData?.halted;
  // placeholderData keeps the last good read on later failures, so no data +
  // error means the fleet endpoint has never answered (e.g. workers host down).
  const fleetOffline = !armyStatusData && armyError;

  const { data: runsData } = useQuery({
    queryKey: ["header-runs"],
    queryFn: () => admin.getRuns(10) as Promise<{ runs?: Run[] }>,
    enabled: enabled && !!isAdmin,
    refetchInterval: enabled && !!isAdmin ? 30000 : false,
    retry: false,
    placeholderData: (prev) => prev,
  });
  const runs: Run[] = runsData?.runs ?? [];
  const runningCount = runs.filter((r) => !r.finished_at && r.status !== "failed").length;

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!target || !document.contains(target)) return;
      if (notifRef.current && !notifRef.current.contains(target)) setNotifOpen(false);
      if (userRef.current && !userRef.current.contains(target)) setUserOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleLogout = async () => {
    await revokeCurrentToken();
    await logout();
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-surface/85 px-4 backdrop-blur-md sm:px-6">
      <button
        type="button"
        onClick={onMenuClick}
        className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:hidden"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      <div className="min-w-0">
        <p className="text-ink-strong text-[15px] font-semibold tracking-tight sm:text-base">
          {title}
        </p>
        <p className="hidden text-xs font-medium text-muted-foreground sm:block">
          Leads Intelligence Platform
        </p>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          title={armyQueued > 0 ? `${armyQueued} leads in flight — open dashboard for live queues` : fleetOffline ? 'Fleet status unreachable — open dashboard' : armyHalted ? 'Army halted — open dashboard' : 'Army idle — open dashboard'}
          aria-label={armyQueued > 0 ? `Army running, ${armyQueued} leads in flight` : fleetOffline ? 'Fleet status unreachable' : 'Army idle'}
          className={`hidden items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] font-semibold transition-colors sm:inline-flex ${armyQueued > 0 ? 'border-success/40 bg-success-soft text-success' : fleetOffline ? 'border-destructive/30 text-destructive hover:text-destructive' : 'border-border text-muted-foreground hover:text-foreground'}`}
        >
          <span className={`h-1.5 w-1.5 rounded-sm ${armyQueued > 0 ? 'animate-pulse bg-success' : fleetOffline ? 'bg-destructive' : 'bg-muted-foreground'}`} />
          {armyQueued > 0 ? `${armyQueued} running` : fleetOffline ? 'Fleet offline' : armyHalted ? 'Halted' : 'Idle'}
        </button>

        {/* Theme Toggle Button header */}
        <button
          type="button"
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          aria-label={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          className="rounded-md border border-border bg-surface p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          {theme === 'dark' ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
        </button>

        {isAdmin && (
          <div className="relative" ref={notifRef}>
            <button
              type="button"
              onClick={() => setNotifOpen((v) => !v)}
              className="relative rounded-md border border-border bg-surface p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              aria-label="Automation runs"
            >
              <Bell className="h-[18px] w-[18px]" />
              {runningCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-sm bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
                  {runningCount > 9 ? "9+" : runningCount}
                </span>
              )}
            </button>
            {notifOpen && (
              <div className="absolute right-0 mt-2 w-80 origin-top-right rounded-md border border-border bg-surface shadow-popover animate-scale-in">
                <div className="border-b px-4 py-2.5">
                  <p className="text-[13px] font-semibold">Automation runs</p>
                  <p className="text-xs text-muted-foreground">
                    {runningCount > 0 ? `${runningCount} running now` : "All runs complete"}
                  </p>
                </div>
                <div className="max-h-72 overflow-y-auto p-1.5">
                  {runs.length === 0 && (
                    <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                      No runs yet
                    </p>
                  )}
                  {runs.map((run) => {
                    const done = !!run.finished_at;
                    return (
                      <div
                        key={run.id}
                        className="flex items-start gap-2.5 rounded-md px-2.5 py-2 transition-colors hover:bg-accent"
                      >
                        {done ? (
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                        ) : (
                          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[13px] font-medium capitalize">
                            {(run.run_type ?? "manual").replace(/_/g, " ")}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {done
                              ? `Finished ${formatDateTime(run.finished_at)}`
                              : `Started ${formatDateTime(run.started_at)}`}
                          </p>
                        </div>
                        {(run.status === "failed" || run.status === "stopped") && (
                          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="relative" ref={userRef}>
          <button
            type="button"
            onClick={() => setUserOpen((v) => !v)}
            className="flex items-center gap-2 rounded-md border border-border bg-surface p-1 pr-2 transition-colors hover:bg-accent"
          >
            <Avatar name={user?.email} size="sm" />
            <span className="hidden max-w-32 truncate text-[13px] font-medium text-foreground lg:block">
              {user?.email}
            </span>
            <ChevronDown className={cn("hidden h-4 w-4 text-muted-foreground transition-transform lg:block", userOpen && "rotate-180")} />
          </button>
          {userOpen && (
            <div className="absolute right-0 mt-2 w-52 origin-top-right rounded-md border border-border bg-surface shadow-popover animate-scale-in">
              <div className="border-b px-4 py-3">
                <p className="truncate text-[13px] font-semibold text-foreground">{user?.email}</p>
                <p className="text-xs capitalize text-muted-foreground">{user?.role}</p>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="w-full rounded-b-md px-4 py-2.5 text-left text-[13px] font-medium text-destructive transition-colors hover:bg-destructive-soft"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;